import { useMemo } from 'react';
import {
  Users,
  Phone,
  MessageSquare,
  Zap,
  BarChart3,
  Shield,
  Clock,
} from 'lucide-react';
import type { Artifact, AccountInsights } from '@/types';

interface InsightsCardProps {
  artifact: Artifact;
}

interface InsightItem {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtext?: string;
  trend?: 'up' | 'down' | 'neutral';
}

export function InsightsCard({ artifact }: InsightsCardProps) {
  const insights = useMemo<InsightItem[]>(() => {
    try {
      const data = JSON.parse(artifact.content) as AccountInsights;

      return [
        {
          icon: <Users className="h-5 w-5" />,
          label: 'Agents',
          value: data.agents,
          subtext: `${data.licenses.assigned}/${data.licenses.total} licenses`,
        },
        {
          icon: <Phone className="h-5 w-5" />,
          label: 'Queues',
          value: data.queues,
          subtext: 'Voice & chat',
        },
        {
          icon: <Zap className="h-5 w-5" />,
          label: 'Skills',
          value: data.skills,
          subtext: 'Active skills',
        },
        {
          icon: <MessageSquare className="h-5 w-5" />,
          label: 'Flows',
          value: data.flows,
          subtext: 'IVR & chat flows',
        },
      ];
    } catch {
      // If parsing fails, try to display as simple key-value pairs
      const lines = artifact.content.split('\n').filter(Boolean);
      return lines.map((line) => {
        const [label, value] = line.split(':').map((s) => s.trim());
        return {
          icon: <BarChart3 className="h-5 w-5" />,
          label: label ?? 'Item',
          value: value ?? line,
        };
      });
    }
  }, [artifact.content]);

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-400" />
          <span className="font-medium text-gray-100">
            {artifact.title ?? 'Account Insights'}
          </span>
        </div>
      </div>

      {/* Grid of insights */}
      <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4">
        {insights.map((insight, index) => (
          <InsightTile key={index} {...insight} />
        ))}
      </div>

      {/* Additional info if available */}
      {artifact.metadata && (
        <div className="border-t border-gray-700 px-4 py-3">
          <div className="flex flex-wrap gap-4 text-xs text-gray-400">
            {artifact.metadata.lastUpdated !== null && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Updated {String(artifact.metadata.lastUpdated)}
              </span>
            )}
            {artifact.metadata.accountName !== null && (
              <span className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                {String(artifact.metadata.accountName)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function InsightTile({ icon, label, value, subtext, trend }: InsightItem) {
  return (
    <div className="flex flex-col rounded-lg bg-gray-900 p-3">
      <div className="mb-2 flex items-center gap-2 text-gray-400">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-100">{value}</span>
        {trend && (
          <span
            className={`text-xs ${
              trend === 'up'
                ? 'text-green-400'
                : trend === 'down'
                ? 'text-red-400'
                : 'text-gray-400'
            }`}
          >
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '−'}
          </span>
        )}
      </div>
      {subtext && (
        <span className="mt-1 text-xs text-gray-400">{subtext}</span>
      )}
    </div>
  );
}
